import { DrawerLink } from '../../app/DrawerLink';
import { Character } from '../character/Character';
import { fromSeed, normalise } from '../character/config';
import type { Profile } from './types';

/**
 * Somebody's character, at the top right of their profile.
 *
 * ## Why here
 *
 * The avatar is a picture of the artist; this is who they are in a room. Both
 * belong on the same edge of the banner - one at each end - and putting the
 * character anywhere else would make it a decoration rather than the thing
 * people will actually recognise in a crowd.
 *
 * ## Yours opens the maker
 *
 * On your own profile it is a way into building it, in a drawer over the page
 * it will be standing on. On anybody else's it is a portrait and nothing more:
 * no link, no cursor, nothing to press. A character that looked pressable and
 * did nothing would be worse than one that plainly does not.
 */
export function ProfileCharacter({
  profile,
  isOwner,
}: {
  profile: Profile;
  isOwner: boolean;
}) {
  /*
   * Seeded from the username until they build one, so every profile has a
   * character from the day it exists rather than a gap where one might go.
   */
  const config = profile.character ? normalise(profile.character) : fromSeed(profile.username);

  const figure = (
    <Character
      config={config}
      size={104}
      crop="full"
      anim="idle"
      className="h-auto w-[88px] xl:w-[104px]"
    />
  );

  /*
   * Anchored to the banner's edge rather than sat in the row.
   *
   * The row is pulled up by `-mt-14` and aligned to its bottom, so a margin
   * would have to know how tall the row is - and the row is as tall as
   * whichever of the avatar and the character is bigger, which changes with the
   * viewport. `bottom: calc(100% - 3.5rem)` is the same fourteen the row was
   * pulled up by, measured from the other end: it puts the feet on the banner's
   * bottom edge whatever the row does.
   *
   * `right-0` is the container's right edge, so it stays inside the same
   * measure as everything else on the page.
   */
  const place =
    'absolute right-0 bottom-[calc(100%-3.5rem)] hidden sm:block';

  if (!isOwner) {
    return (
      <div data-profile-character className={place} title={profile.display_name}>
        {figure}
      </div>
    );
  }

  return (
    <DrawerLink
      to="/character"
      data-profile-character="own"
      title="Change your character"
      className={`${place} transition-transform hover:-translate-y-1`}
    >
      {figure}
    </DrawerLink>
  );
}
